import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useIntl, useLocation, useSelector, history, getLocale } from 'umi';
import { Guide } from '@/pages/home/components/Guide';
import {
  INTELLIGENCE_GUIDE_HIDE,
  INTELLIGENCE_GUIDE_BOX_LIST3,
} from '@/pages/home/components/GuideModule/GuideIntelligenceBox';
import withDelayWrapper from './DelayWrapper';
import {
  // GUIDE_SYS_INTELLIGENCE,
  GUIDE_TYPE_INTELLIGENCE_GENRE_MARKET_PROFILE,
} from '@/pages/home/models/guide';
import { storage } from '@/utils/utils';
import { FROM_PREFIX, parseUserGuideStep, generateURLByUserGuideStep } from './utils';
// import { LANGUAGE_CN } from '@intelligence/_config/config';

import type { IGuideFrom } from './types';

import '@op/components/UserGuide/index.scss';

export const GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES = {
  ELEMENT1: 'dbi-user-guide__genre-profile-table',
  ELEMENT1_HIGHLIGHT1: 'dbi-user-guide__menu-genre-profile',
  ELEMENT1_HIGHLIGHT2: 'dbi-user-guide__genre-profile-tab-pc-games',
  ELEMENT1_HIGHLIGHT3: 'dbi-user-guide__genre-profile__filter-genre-select',
  ELEMENT1_HIGHLIGHT4: 'dbi-user-guide__genre-profile__filter-genre-type-select__main-sub-genre',
  ELEMENT2: 'dbi-user-guide__genre-profile-details__segment1-pc-game-market',
  ELEMENT2_HIGHLIGHT1: 'dbi-user-guide__genre-profile-details__tab-insights',
  ELEMENT3: 'dbi-user-guide__genre-profile-details__segment-by',
  ELEMENT3_HIGHLIGHT1: 'dbi-user-guide__menu-genre-profile',
  ELEMENT3_HIGHLIGHT2: 'dbi-user-guide__genre-profile-details__tab-stats',
  ELEMENT3_HIGHLIGHT3: 'dbi-user-guide__genre-profile-details__segment2-pc-game-market',
  ELEMENT4: 'dbi-user-guide__market-profile__table',
  ELEMENT4_HIGHLIGHT1: 'dbi-user-guide__menu-market-profile',
  ELEMENT4_HIGHLIGHT2: 'dbi-user-guide__market-profile-tab-mobile-games',
  ELEMENT4_HIGHLIGHT3: 'dbi-user-guide__market-profile-tab-pc-games',
  ELEMENT4_HIGHLIGHT4: 'dbi-user-guide__market-profile-tab-console-games',
  ELEMENT5: 'dbi-user-guide__market-profile-tab-demographics',
};

const STEP1_PATH = '/intelligence/genreProfile/pcGames';
const STEP2_PATH =
  '/intelligence/genreProfile/pcGames/detail/insights?entityType=pc&genre=Shooter&genreType=main&source=iegg';
const STEP3_PATH =
  '/intelligence/genreProfile/pcGames/detail/stats?entityType=pc&genre=Shooter&genreType=main&source=iegg';
const STEP4_PATH = '/intelligence/marketProfile/MobileGames';
const STEP5_PATH =
  '/intelligence/marketProfile/Demographics/detail?marketName=United%20States&marketNameEn=us&pageId=MarketProfilMobile&segment=mobile';
const STEP_PATH_LIST = [STEP1_PATH, STEP2_PATH, STEP3_PATH, STEP4_PATH, STEP5_PATH];
const USER_GUIDE_FROMS_CHARS = Array.from({ length: STEP_PATH_LIST.length - 1 }).map(
  (item, idx) => `${FROM_PREFIX}_${idx + 1}`,
);

/**
 * 用户引导组件 - 类型市场画像引导
 * 
 * 该组件负责展示类型市场画像页面的新手引导流程，包含以下主要功能：
 * - 管理引导步骤的状态和展示
 * - 处理用户在引导过程中的前进、后退、完成等操作
 * - 根据用户ID和本地存储判断是否需要显示引导
 * - 支持多语言显示
 * - 自动定位和高亮关键UI元素
 * 
 * @component
 * @returns {JSX.Element} 返回Guide组件实例
 */
const GuideGenreMarketProfileContext = () => {
  const location: any = useLocation();
  const { pathname, query } = location;
  const { from } = query || {};
  const initialUserGuideStep = parseUserGuideStep(from);

  const { formatMessage } = useIntl();
  const locale = getLocale();
  // const isCN = locale === LANGUAGE_CN;

  const dispatch = useDispatch();

  const { guideType, userInfo } = useSelector((state: any) => {
    const guideState = state?.guide;
    const homeState = state?.home;

    return {
      guideType: guideState?.guideType,
      userInfo: homeState?.userInfo,
    };
  });

  const userId = userInfo?.openid;

  const [currentGuideStep, setCurrentGuideStep] = useState(-1);

  const steps = useMemo<any[]>(
    () => [
      {
        highlightElements: [
          {
            element: document.querySelector(`.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT1_HIGHLIGHT1}`),
          },
          {
            element: document.querySelector(`.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT1_HIGHLIGHT2}`),
            padding: {
              paddingLeft: 20,
              paddingRight: 20,
            },
          },
          {
            element: document.querySelector(`.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT1_HIGHLIGHT3}`),
            hasRadius: true,
          },
          {
            element: document.querySelector(`.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT1_HIGHLIGHT4}`),
            padding: {
              paddingTop: 10,
              paddingRight: 10,
              paddingBottom: 10,
              paddingLeft: 10,
            },
          },
        ],
        element: () =>
          document.querySelector(
            `.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT1} > .tea-table__body > .tea-table__box > tbody > tr:first-of-type > td:nth-of-type(3)`,
          ),
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 3 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_3_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_3_1_desc' }),
        placement: 'top-center',
        bubbleStyle: {},
        autoScrollIntoView: false,
      },
      {
        highlightElements: [
          {
            element: document.querySelector(`.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT2_HIGHLIGHT1}`),
            padding: {
              paddingLeft: 20,
              paddingRight: 20,
            },
          },
        ],
        element: () => document.querySelector(`.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT2}`),
        padding: {
          paddingTop: 10,
          paddingRight: 10,
          paddingBottom: 10,
          paddingLeft: 10,
        },
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 3 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_3_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_3_2_desc' }),
        placement: 'bottom-center',
        placementOffset: [10, 20],
        bubbleStyle: {},
      },
      {
        highlightElements: [
          {
            element: document.querySelector(`.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT3_HIGHLIGHT1}`),
          },
          {
            element: document.querySelector(`.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT3_HIGHLIGHT2}`),
            padding: {
              paddingLeft: 20,
              paddingRight: 20,
            },
          },
          {
            element: document.querySelector(`.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT3_HIGHLIGHT3}`),
            padding: {
              paddingTop: 10,
              paddingRight: 10,
              paddingBottom: 10,
              paddingLeft: 10,
            },
          },
        ],
        element: () => document.querySelector(`.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT3}`),
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 3 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_3_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_3_3_desc' }),
        placement: 'bottom-center',
        bubbleStyle: {},
      },
      {
        highlightElements: [
          {
            element: document.querySelector(`.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT4_HIGHLIGHT1}`),
          },
          {
            element: document.querySelector(`.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT4_HIGHLIGHT2}`),
            padding: {
              paddingLeft: 20,
              paddingRight: 20,
            },
          },
          {
            element: document.querySelector(`.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT4_HIGHLIGHT3}`),
            padding: {
              paddingLeft: 20,
              paddingRight: 20,
            },
          },
          {
            element: document.querySelector(`.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT4_HIGHLIGHT4}`),
            padding: {
              paddingLeft: 20,
              paddingRight: 20,
            },
          },
        ],
        element: () =>
          document.querySelector(
            `.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT4} > .tea-table__body tbody > tr:nth-of-type(3) > td:nth-of-type(2)`,
          ),
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 3 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_3_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_3_4_desc' }),
        placement: 'top-start',
        bubbleStyle: {},
      },
      {
        element: () => document.querySelector(`.${GENRE_MARKET_PROFILE_GUIDE_CLASSNAMES.ELEMENT5}`),
        title: formatMessage({ id: 'dbi_user_guide_title_core_function' }, { num: 3 }),
        subtitle: formatMessage({ id: 'dbi_user_guide_3_sub_title' }),
        description: formatMessage({ id: 'dbi_user_guide_3_5_desc' }),
        padding: {
          paddingLeft: 20,
          paddingRight: 20,
        },
        placement: 'bottom-center',
        bubbleStyle: {},
      },
    ],
    [locale],
  );

  /**
   * 处理用户引导完成的回调函数
   * 将用户ID存入本地存储的引导列表中,并更新引导模块显示状态
   * 
   * @description
   * 1. 检查是否有userId
   * 2. 从localStorage获取引导列表
   * 3. 如果userId不在列表中则添加
   * 4. 更新localStorage
   * 5. 通过dispatch更新引导模块状态
   */
  const handleFinished = () => {
    if (userId) {
      let list = storage.getLocalStorageItem(INTELLIGENCE_GUIDE_BOX_LIST3);
      list = Array.isArray(list) ? list : [];
      if (!list.includes(userId)) {
        list.push(userId);
      }
      storage.setLocalStorageItem(INTELLIGENCE_GUIDE_BOX_LIST3, list);
    }
    dispatch({
      type: 'guide/showIntelligenceGuideModule',
      payload: true,
    });
  };

  // const handleCloseStep = () => {
  //   dispatch({
  //     type: 'guide/save',
  //     payload: {
  //       guideSys: GUIDE_SYS_INTELLIGENCE,
  //       guideType: '',
  //     },
  //   });
  // };

  /**
   * 处理用户引导步骤的返回操作
   * @param current 当前步骤
   * @description
   * 1. 如果前一个路径与当前路径相同,则根据步骤生成新URL并替换
   * 2. 如果从步骤3返回步骤2,则生成步骤2对应的URL并替换
   * 3. 其他情况使用浏览器默认的返回操作
   */
  const handleBack = (current: number) => {
    const prevPath = STEP_PATH_LIST[current];
    if (prevPath === pathname) {
      const path = generateURLByUserGuideStep(prevPath, current);
      history.replace(path);
      return;
    }
    if (STEP2_PATH.startsWith(prevPath) && STEP3_PATH.startsWith(pathname)) {
      const path = generateURLByUserGuideStep(STEP2_PATH, current);
      history.replace(path);
      return;
    }
    history.goBack();
  };

  /**
   * 处理用户引导步骤的跳转逻辑
   * @param current 当前步骤索引
   * @description
   * 1. 根据当前步骤索引获取下一步的路径
   * 2. 判断路由跳转方式:
   *    - 如果当前路径与下一步路径相同,或者从步骤2跳转到步骤3,使用replace
   *    - 其他情况使用push
   * 3. 生成带有用户引导步骤参数的URL并进行跳转
   */
  const handleNext = (current: number) => {
    if (current >= 0) {
      const { pathname } = location;
      const nextPathname = STEP_PATH_LIST[current];
      const routeAction =
        pathname === nextPathname || (STEP2_PATH.startsWith(pathname) && STEP3_PATH.startsWith(nextPathname))
          ? 'replace'
          : 'push';
      const routePath = generateURLByUserGuideStep(nextPathname, current);
      history[routeAction](routePath);
    }
  };

  /**
   * 处理用户引导步骤变化的回调函数
   * @param current - 当前步骤的索引值
   * @param context - 上下文对象,包含步骤变化的来源
   * @param context.from - 步骤变化的来源,可选值:'back'(返回),'next'(下一步),'finish'(完成),'cancel'(取消)
   */
  const handleCurrentChange = (current: number, context: { from: IGuideFrom }) => {
    const { from } = context || {};
    setCurrentGuideStep(current);
    if (from === 'back') {
      handleBack(current);
    } else if (from === 'next') {
      handleNext(current);
    } else if (from === 'finish') {
      handleFinished();
    } else if (from === 'cancel') {
      // handleCloseStep();
      handleFinished();
    }
  };

  // 注意下面两个useEffect不要合并到一起，不然会出现：从头部导航进入新手引导，点击到下一步时，关闭按钮无法关闭，而是返回到第一步
  useEffect(() => {
    if (guideType !== GUIDE_TYPE_INTELLIGENCE_GENRE_MARKET_PROFILE) return;
    if (pathname !== STEP1_PATH && !USER_GUIDE_FROMS_CHARS.includes(from)) return;

    setCurrentGuideStep(initialUserGuideStep);
  }, [guideType, pathname, from, initialUserGuideStep]);

  useEffect(() => {
    const list = storage.getLocalStorageItem(INTELLIGENCE_GUIDE_BOX_LIST3);
    const guideHide = storage.getLocalStorageItem(INTELLIGENCE_GUIDE_HIDE);
    if (!userId || list?.includes(userId) || guideHide) return;
    if (pathname !== STEP1_PATH && !USER_GUIDE_FROMS_CHARS.includes(from)) return;

    setCurrentGuideStep(initialUserGuideStep);
  }, [userId, pathname, from, initialUserGuideStep]);

  return (
    // finishText 根据中英文调整
    <Guide
      visibleCloseBtn
      hideCancelButton
      showBackButton
      nextButtonTheme="primary"
      backText={formatMessage({ id: 'guide_last_step' })}
      nextText={formatMessage({ id: 'guide_next_step' })}
      finishText={formatMessage({ id: 'guide_more_step' })}
      steps={steps}
      current={currentGuideStep}
      onCurrentChange={handleCurrentChange}
    />
  );
};

const GuideGenreMarketProfile = () => {
  const location = useLocation();

  const { guideType } = useSelector((state: any) => {
    const guideState = state?.guide;
    return {
      guideType: guideState?.guideType,
    };
  });
  if (guideType !== GUIDE_TYPE_INTELLIGENCE_GENRE_MARKET_PROFILE) return null;
  if (
    !STEP_PATH_LIST.some((item) => {
      const searchIdx = item?.indexOf('?');
      const pathname = searchIdx === -1 ? item : item?.substring(0, searchIdx);
      return pathname === location.pathname;
    })
  )
    return null;

  return <GuideGenreMarketProfileContext />;
};

export const GuideGenreMarketProfileDelay = withDelayWrapper(GuideGenreMarketProfile);

export default GuideGenreMarketProfile;
